import { __rest } from 'tslib';
import React, { useContext, useRef, useState, useMemo, useCallback } from 'react';
import cs from 'classnames';
import PhotoSwipe from 'photoswipe';
import DefaultPhotoSwipeUI from 'photoswipe/dist/photoswipe-ui-default';

let defaultGalleryContext = {
    openLightBox: () => { },
    modifiedSlides: null,
};
let GalleryContext = React.createContext(defaultGalleryContext);
let Provider = GalleryContext.Provider;
function useGalleryContext() {
    return useContext(GalleryContext);
}

let Gallery = React.forwardRef((props, ref) => {
    let { className, slides, options = {} } = props, rest = __rest(props, ["className", "slides", "options"]);
    let rootRef = useRef(null);
    const [photoSwipe, setPhotoSwipe] = useState(null);
    // @ts-ignore
    ref === null || ref === void 0 ? void 0 : ref.current = photoSwipe;
    let modifiedSlides = useMemo(() => slides
        .filter(x => x)
        .map(slide => {
        var _a, _b;
        return (Object.assign({ w: Number((_a = slide.size) === null || _a === void 0 ? void 0 : _a.split('x')[0]), h: Number((_b = slide.size) === null || _b === void 0 ? void 0 : _b.split('x')[1]) }, slide));
    }), [slides]);
    // It seems wasteful to recreate the PhotoSwipe object every time we want to open
    // the light box, but every bit of PhotoSwipe documentation I can find does it this
    // way.  Additionally, despite numerous attempts (over several years lol) I can't
    // seem to ever get the light box to open again with the same PhotoSwipe instance.
    // See: https://photoswipe.com/documentation/getting-started.html
    const openLightBox = useCallback((slide) => {
        let lightBoxElement = rootRef.current.querySelector('.pswp');
        let photoSwipe = new PhotoSwipe(lightBoxElement, DefaultPhotoSwipeUI, modifiedSlides, Object.assign({ index: slides.findIndex(s => s.src === slide.src) }, options));
        photoSwipe.init();
        setPhotoSwipe(photoSwipe);
    }, []);
    return (React.createElement(Provider, { value: { openLightBox, modifiedSlides } },
        React.createElement("div", Object.assign({}, rest, { className: cs('pwsp-gallery', className), ref: rootRef }))));
});

/**
 * This HTML is taken directly from the Photoswipe docs:
 * https://photoswipe.com/documentation/getting-started.html
 */
function LightBox(props) {
    let { className } = props, rest = __rest(props, ["className"]);
    return (React.createElement("div", Object.assign({}, rest, { className: cs('pswp', className), tabIndex: -1, role: "dialog" }),
        React.createElement("div", { className: "pswp__bg" }),
        React.createElement("div", { className: "pswp__scroll-wrap" },
            React.createElement("div", { className: "pswp__container" },
                React.createElement("div", { className: "pswp__item" }),
                React.createElement("div", { className: "pswp__item" }),
                React.createElement("div", { className: "pswp__item" })),
            React.createElement("div", { className: "pswp__ui pswp__ui--hidden" },
                React.createElement("div", { className: "pswp__top-bar" },
                    React.createElement("div", { className: "pswp__counter" }),
                    React.createElement("button", { className: "pswp__button pswp__button--close", title: "Close (Esc)" }),
                    React.createElement("button", { className: "pswp__button pswp__button--share", title: "Share" }),
                    React.createElement("button", { className: "pswp__button pswp__button--fs", title: "Toggle fullscreen" }),
                    React.createElement("button", { className: "pswp__button pswp__button--zoom", title: "Zoom in/out" }),
                    React.createElement("div", { className: "pswp__preloader" },
                        React.createElement("div", { className: "pswp__preloader__icn" },
                            React.createElement("div", { className: "pswp__preloader__cut" },
                                React.createElement("div", { className: "pswp__preloader__donut" }))))),
                React.createElement("div", { className: "pswp__share-modal pswp__share-modal--hidden pswp__single-tap" },
                    React.createElement("div", { className: "pswp__share-tooltip" })),
                React.createElement("button", { className: "pswp__button pswp__button--arrow--left", title: "Previous (arrow left)" }),
                React.createElement("button", { className: "pswp__button pswp__button--arrow--right", title: "Next (arrow right)" }),
                React.createElement("div", { className: "pswp__caption" },
                    React.createElement("div", { className: "pswp__caption__center" }))))));
}

function Thumbnail(props) {
    let { slide: originalSlide, onClick, className, classes = {}, anchorProps = {}, imageProps = {}, style = {}, aspectRatioMultiplier } = props, rest = __rest(props, ["slide", "onClick", "className", "classes", "anchorProps", "imageProps", "style", "aspectRatioMultiplier"]);
    const { openLightBox, modifiedSlides } = useGalleryContext();
    const slide = useMemo(() => modifiedSlides.find(mfSlide => mfSlide.src === originalSlide.src), [originalSlide, modifiedSlides]);
    function handleClick(e) {
        // Don't navigate to the url on the anchor tag to go to
        // flicker or wherever the image is hosted
        e.preventDefault();
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
        openLightBox(slide);
    }
    return (React.createElement("figure", Object.assign({ itemProp: "associatedMedia", itemType: "http://schema.org/ImageObject", onClick: handleClick, className: cs('pwsp-thumbnail', className, classes.root), style: Object.assign({ width: aspectRatioMultiplier
                ? aspectRatioMultiplier * (slide.w / slide.h)
                : undefined }, style) }, rest),
        React.createElement("a", Object.assign({ href: slide.src, itemProp: "contentUrl" }, anchorProps, { className: cs(classes.anchor, anchorProps.className) }),
            React.createElement("img", Object.assign({ src: slide.msrc || slide.src, alt: slide.title, itemProp: "thumbnail", width: slide.w, height: slide.h }, imageProps, { className: cs(classes.image, imageProps.className), style: Object.assign({ aspectRatio: `${slide.w} / ${slide.h}` }, ((imageProps === null || imageProps === void 0 ? void 0 : imageProps.style) || {})) })))));
}

export { Gallery, LightBox, Thumbnail };
